<template>
  <article
    class="result"
    :aria-label="title"
  >
    <div
      class="result__thumbnail result__thumbnail--desktop"
      :class="{ 'result__thumbnail--end': thumbnailPosition === 'end' }"
    >
      <slot name="thumbnail" />
    </div>
    <div class="result__body">
      <div class="result__header">
        <div class="result__info">
          <slot name="info-start" />
          <a
            class="result__link"
            data-test-id="result-link"
            v-bind="linkAttributes"
            tabindex="-1"
            @click="$emit('title-click')"
            @auxclick="$emit('title-click')"
          >
            <span
              class="result__source source__content--top"
              data-test-id="result-source"
            >
              <span
                class="source__content--domain"
              >{{ urlParts.domain }}</span><span
                class="source__content--breadcrumbs"
              >{{ urlParts.breadcrumbs }}
              </span>
            </span>
          </a>
          <slot name="info-end" />
        </div>
        <div class="result__title">
          <ELink
            class="result__link"
            color="result"
            data-test-id="result-link"
            v-bind="linkAttributes"
            @click="$emit('title-click')"
            @auxclick="$emit('title-click')"
          >
            <h2
              class="result-title__heading"
              data-test-id="result-title"
            >
              {{ title }}
            </h2>
          </ELink>
          <slot name="title-end" />
        </div>
      </div>
      <div class="result__columns">
        <div class="result__columns-start">
          <div class="result__content-outer">
            <div
              class="result__thumbnail result__thumbnail--mobile"
              :class="{ 'result__thumbnail--end': thumbnailPosition === 'end' }"
            >
              <slot name="thumbnail" />
            </div>
            <div class="result__content-inner">
              <div class="result__description" data-test-id="result-description">
                <slot name="description" />
              </div>
            </div>
          </div>
          <slot name="content-end" />
        </div>
        <div v-if="$slots['columns-end']" class="result__columns-end">
          <slot name="columns-end" />
        </div>
      </div>
      <div v-if="hasExtraContent" class="result__extra-content">
        <slot />
      </div>
    </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import { sanitizeHTML } from '~~/common/js/universal/utils';

import ELink from '~~/common/vue/components/link';

const thumbnailPositions = ['start', 'end'];

export const baseProps = {
  source: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  rank: {
    type: [Number, null],
    default: null,
  },
  pagePosition: {
    type: [String, null],
    default: null,
  },
  thumbnailPosition: {
    type: String,
    default: 'start',
    validator: (position) => thumbnailPositions.includes(position),
  },
};

export default {
  name: 'EBaseResult',
  components: {
    ELink,
  },
  props: {
    ...baseProps,
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    infoShown() {
      const { infoStart, infoEnd } = this.$slots;
      return infoStart || infoEnd || this.source;
    },
    linkAttributes() {
      return {
        href: this.url,
        target: this.linkTarget,
        rel: 'noopener',
      };
    },
    hasExtraContent() {
      return !!this.$slots.default;
    },
    sanitizedSource() {
      // RegExp pattern in sanitizeHTML can't currently handle
      // an empty array and will leave the given string untouched.
      // So this is definitely just a workaround.
      return sanitizeHTML(this.source, ['div']);
    },
    urlParts() {
      // get rid of empty matches and the ones only
      // containing a slash (from trailing slash at URL)
      const urlParts = this.sanitizedSource
        .split(/(^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+))/)
        .filter((match) => match !== '' && match !== '/');

      // skipping urlParts[1] as this capture group
      // won't contain the protocoll (http/https)
      const domain = urlParts[0] ?? '';
      const breadcrumbs = urlParts[2]?.replace(/\//g, ' › ') ?? '';

      return { domain, breadcrumbs };
    },
  },
};
</script>

<style lang="scss" scoped>
.result {
  display: flex;
  padding: $space-s;
}

.result__header {
  padding: $space-2s;
}

.result__info,
.result__title {
  position: relative;
}

.result__link {
  border-radius: $border-radius-s;
  display: inline-flex;

  &:focus {
    @extend %keyboard-nav-focus;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .result__info & {
    @extend %ellipsis;
  }
}

.result__title {
  display: inline-flex;
  justify-content: space-between;
  width: 100%;

  @include desktop {
    justify-content: flex-start;
  }
}

.result-title__heading {
  @include line-clamp(2);

  margin: 0;
  font-size: $font-l;
  font-weight: $font-weight-m;

  @include desktop {
    font-size: $font-1l;
  }
}

.result__thumbnail--mobile {
  @include tablet {
    display: none;
  }
}

.result__thumbnail--desktop {
  display: none;

  @include tablet {
    display: block;

    &:not(:empty) {
      margin-left: $space-2s;
    }
  }
}

.result__thumbnail--end {
  order: 1;

  &:not(:empty) {
    margin-left: 0;
  }
}

.result__body {
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.result__columns {
  order: 1;
  display: flex;
  align-items: center;
  padding: 0 $space-2s;

  &:last-child {
    padding-bottom: $space-2s;

    @include desktop {
      padding-bottom: 0;
    }
  }
}

.result__columns-start {
  // constrain the flex child to parent's size
  min-width: 0;
}

.result__columns-end {
  display: none;
  margin-left: $space-l;

  @include tablet {
    display: block;
  }
}

.result__content-outer {
  display: flex;
}

.result__content-inner {
  // constrain the flex child to parent's size
  min-width: 0;
}

.result__info {
  align-items: center;
  display: inline-flex;
  font-size: $font-m;
  line-height: $line-height-s;
  margin-bottom: $space-2s;
  // account for the possible domain label
  width: calc(100% - #{$space-m});

  @include desktop {
    width: 100%;
  }

  .tooltip {
    font-size: $font-m;
  }

  > :not(:first-child) {
    &::before {
      content: "·";
      margin: 0 $space-2s;
    }
  }
}

.result__source {
  @extend %ellipsis;

  color: var(--color-text-secondary);
  display: block;

  .source__content--domain {
    color: var(--color-url);
  }
}

.result__description {
  display: block;
}

.result__extra-content {
  order: 1;
  padding: $space-2s;
}
</style>
